import React from "react";
import { Link } from "react-router-dom";
import { usePageTitle } from "../hooks/usePageTitle";

const DonateCancel = () => {
  usePageTitle("Donation Cancelled");

  return (
    <div className="max-w-4xl mx-auto px-4 py-12 min-h-[60vh] flex items-center justify-center">
      <div className="text-center">
        {/* Info Icon */}
        <div className="mb-6 flex justify-center">
          <div className="bg-orange-500/20 p-6 rounded-full">
            <svg
              className="w-16 h-16 text-orange-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
        </div>

        {/* Cancelled Message */}
        <h1 className="text-4xl font-bold text-white mb-4">
          Donation Cancelled
        </h1>
        <p className="text-xl text-gray-300 mb-6">
          No worries! Your donation was not processed.
        </p>

        {/* Card with Message */}
        <div className="bg-dark-card border border-gray-700 rounded-lg p-8 mb-8 max-w-2xl mx-auto">
          <p className="text-gray-300 mb-4 text-lg">
            We understand! Supporting us is completely optional.
          </p>
          <p className="text-gray-400 mb-6">
            Collect Arena will always remain free for everyone. Your enjoyment
            of the platform is what matters most to us!
          </p>

          {/* Other Ways to Support */}
          <div className="border-t border-gray-700 pt-6">
            <h2 className="text-xl font-semibold text-white mb-4">
              Other Ways to Support
            </h2>
            <ul className="text-left text-gray-300 space-y-3">
              <li className="flex items-start gap-3">
                <svg
                  className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z"
                  />
                </svg>
                <div>
                  <strong className="text-white">Join our community</strong>
                  <p className="text-sm text-gray-400">
                    Connect with other quiz enthusiasts on Discord and Reddit
                  </p>
                </div>
              </li>

              <li className="flex items-start gap-3">
                <svg
                  className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div>
                  <strong className="text-white">Share with friends</strong>
                  <p className="text-sm text-gray-400">
                    Tell others about Collect Arena and help us grow
                  </p>
                </div>
              </li>

              <li className="flex items-start gap-3">
                <svg
                  className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                  />
                </svg>
                <div>
                  <strong className="text-white">Suggest new lists</strong>
                  <p className="text-sm text-gray-400">
                    Help us create content you want to play!
                  </p>
                </div>
              </li>

              <li className="flex items-start gap-3">
                <svg
                  className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                  />
                </svg>
                <div>
                  <strong className="text-white">Leave feedback</strong>
                  <p className="text-sm text-gray-400">
                    Your suggestions help make Collect Arena better!
                  </p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Call to Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Link
            to="/"
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition w-full sm:w-auto"
          >
            Browse Lists
          </Link>

          <a
            href="https://discord.gg/473F5RGH99"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-8 rounded-lg transition w-full sm:w-auto"
          >
            Join Community
          </a>
        </div>
      </div>
    </div>
  );
};

export default DonateCancel;
